import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { format, getISOWeek, getYear, subMonths, startOfMonth, differenceInCalendarDays, isValid } from 'date-fns';
import { getUserGymId } from '@/utils/gymAuth';
import { getCurrentUserId } from '@/utils/authHelpers';

// Types à adapter à ton modèle
interface AthleteDashboardState {
  isLoading: boolean;
  error: string | null;
  // Stats globales
  totalClasses: number;
  averageRating: number;
  streak: number;
  longestStreak: number;
  totalHours: number;
  bookingHistory: any[];
  totalClassesDelta:number;
  firstSessionDate: string | null;
  memberSince: string;
  uniqueClassTypes: number;
  participationRate: number;
  cancellationRate: number;
  avgSessionsPerWeek: string;
  badges: string[];

  // Graphes
  classTypeDistribution: { name: string; value: number; color: string }[];
  activityData: { month: string; classes: number }[];
  // Méthode pour charger
  fetchDashboard: () => Promise<void>;
}

export const useAthleteDashboardStore = create<AthleteDashboardState>((set) => ({
  isLoading: false,
  error: null,
  totalClasses: 0,
  averageRating: 0,
  streak: 0,
  longestStreak: 0,
  totalHours: 0,
  bookingHistory: [],
  classTypeDistribution: [],
  activityData: [],
  totalClassesDelta:0,
  firstSessionDate: null,
  memberSince: '',
  uniqueClassTypes: 0,
  participationRate: 0,
  cancellationRate: 0,
  avgSessionsPerWeek: '',
  badges: [],


  fetchDashboard: async () => {
    set({ isLoading: true, error: null });
    try {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error("User not authenticated");

      const gymId = getUserGymId();
      
      // 1 SEULE REQUÊTE AVEC JOINTURES
      const { data: bookings, error: bookingsError } = await supabase
        .from('class_bookings')
        .select(`
          *,
          scheduled_classes (
            id,
            name,
            instructor,
            duration,
            time,
            categories (
              id,
              name
            )
          ),
          class_attendance (
            id,
            rating,
            notes
          )
        `)
        .eq('user_id', userId)
        .eq('gym_id', gymId)
        .order('class_date', { ascending: true });

      if (bookingsError) throw bookingsError;

      // Initialisations
      let totalMinutes = 0;
      let ratingSum = 0;
      let ratedCount = 0;
      const bookingHistory: any[] = [];
      const categoryMap = new Map<string, number>();
      // Clé = 'yyyy-MM' et NON le libellé court du mois : avec 'Jul' une séance
      // de juillet 2022 tombait dans le seau de juillet 2025 (audit 4.3).
      const monthlyData = new Map<string, number>();
      const now = new Date();

      // Six derniers mois glissants. `subMonths` gère le débordement de fin de
      // mois, contrairement à `setMonth(getMonth() - i)` qui donnait
      // May, May, Mar, Mar, Jan, Dec au 31/05.
      const monthBuckets: { key: string; label: string }[] = [];
      for (let i = 5; i >= 0; i--) {
        const monthDate = startOfMonth(subMonths(now, i));
        const key = format(monthDate, 'yyyy-MM');
        monthBuckets.push({ key, label: format(monthDate, 'MMM') });
        monthlyData.set(key, 0);
      }
      // Borne basse de la fenêtre : sans elle, des réservations vieilles de
      // trois ans gonflaient les barres des six derniers mois.
      const windowStartKey = monthBuckets[0].key;

      // Filtres
      const completedBookings = (bookings || []).filter(b => b.status === 'completed');
      // Jours DISTINCTS de séance : deux séances le même jour cassaient la
      // série au lieu de compter pour un seul jour (audit 4.4).
      const distinctSessionDays = new Set<string>();

      completedBookings.forEach(booking => {
        const sc = booking.scheduled_classes;
        if (!sc) return;
        bookingHistory.push(booking);

        // Total minutes
        totalMinutes += parseInt(sc.duration || '60');

        const bookingDate = new Date(booking.class_date);
        if (isValid(bookingDate)) {
          distinctSessionDays.add(format(bookingDate, 'yyyy-MM-dd'));

          // Activité par mois, restreinte à la fenêtre de 6 mois.
          const monthKey = format(bookingDate, 'yyyy-MM');
          if (monthKey >= windowStartKey && monthlyData.has(monthKey)) {
            monthlyData.set(monthKey, (monthlyData.get(monthKey) || 0) + 1);
          }
        }

        // Répartition par catégorie
        const categoryName = sc.categories?.name || 'Unknown';
        categoryMap.set(categoryName, (categoryMap.get(categoryName) || 0) + 1);

        // Moyenne des notes. `class_attendance` est une relation *to-many* :
        // c'est un TABLEAU. `booking.class_attendance?.rating` valait toujours
        // `undefined` et `[] != undefined` étant vrai, le dénominateur comptait
        // toutes les séances → note figée à 0/5 (audit 4.1).
        // L'edge function lit `class_attendance?.[0]` : on s'aligne.
        const attendance = Array.isArray(booking.class_attendance)
          ? booking.class_attendance[0]
          : booking.class_attendance;
        const rating = Number(attendance?.rating);
        if (Number.isFinite(rating) && rating > 0) {
          ratingSum += rating;
          ratedCount++;
        }
      });

      // Séries : on raisonne en jours distincts triés par ordre croissant.
      const sortedDays = Array.from(distinctSessionDays).sort();
      let maxStreak = 0;
      let runLength = 0;
      let previousDay: Date | null = null;
      for (const day of sortedDays) {
        const dayDate = new Date(`${day}T00:00:00`);
        runLength = previousDay && differenceInCalendarDays(dayDate, previousDay) === 1
          ? runLength + 1
          : 1;
        maxStreak = Math.max(maxStreak, runLength);
        previousDay = dayDate;
      }

      // Série EN COURS : celle qui se termine aujourd'hui ou hier. L'ancien
      // calcul renvoyait la série se terminant à la dernière séance connue —
      // un historique de janvier affichait « série en cours : 3 » en juillet.
      const lastSessionDay = previousDay;
      const daysSinceLastSession = lastSessionDay
        ? differenceInCalendarDays(new Date(format(now, 'yyyy-MM-dd') + 'T00:00:00'), lastSessionDay)
        : null;
      const currentStreak = daysSinceLastSession !== null && daysSinceLastSession <= 1
        ? runLength
        : 0;

      // Date de la 1ʳᵉ séance et ancienneté
      const sortedByDate = completedBookings
      .slice()
      .sort((a, b) => new Date(a.class_date).getTime()  - new Date(b.class_date).getTime() );
      const firstSessionDate = sortedByDate[0]?.class_date || null;

      // Ancienneté (en mois ou années)
      const joinDate = firstSessionDate ? new Date(firstSessionDate) : null;
      const nowDate = new Date();
      const memberSinceMonths = joinDate ? (nowDate.getFullYear() - joinDate.getFullYear()) * 12 + (nowDate.getMonth() - joinDate.getMonth()) : 0;
      const memberSinceHuman = memberSinceMonths >= 12
      ? `${Math.floor(memberSinceMonths / 12)} ans${memberSinceMonths % 12 ? ' et ' + (memberSinceMonths % 12) + ' mois' : ''}`
      : `${memberSinceMonths} mois`;

      // Diversité (nombre de types différents)
      const uniqueClassTypes = new Set(completedBookings.map(b => b.scheduled_classes?.categories?.name)).size;

      // Taux de participation & annulation
      const totalBookings = (bookings || []).length;
      const completedCount = completedBookings.length;
      const cancelledCount = (bookings || []).filter(b => b.status === 'cancelled').length;
      const participationRate = totalBookings ? Math.round((completedCount / totalBookings) * 100) : 0;
      const cancellationRate = totalBookings ? Math.round((cancelledCount / totalBookings) * 100) : 0;

      // Assiduité/semaine (calcul naïf, peut être affiné)
      const weeksMap: Record<string, number> = {};
      completedBookings.forEach(b => {
        const date = new Date(b.class_date);
        // Clef unique : année + numéro de semaine ISO (ex: "2024-W02")
        const year = getYear(date);
        const week = getISOWeek(date);
        const weekKey = `${year}-W${week.toString().padStart(2, "0")}`;
        weeksMap[weekKey] = (weeksMap[weekKey] || 0) + 1;
      });
      
      const nbWeeks = Object.keys(weeksMap).length;
      const avgSessionsPerWeek = nbWeeks
        ? (completedBookings.length / nbWeeks).toFixed(1)
        : "0";

      // Top streak historique → déjà calculé dans `longestStreak`
      // Badges : (exemples)
      const badges :string[] = [];
      if (completedCount >= 50) badges.push("50 séances !");
      if (maxStreak >= 10) badges.push("Streak de 10 jours !");
      if (memberSinceMonths >= 12) badges.push("1 an de fidélité !");


      // Traitements finaux : moyenne sur les seules séances RÉELLEMENT notées.
      const avgRating = ratedCount > 0
        ? Math.round((ratingSum / ratedCount) * 10) / 10
        : 0;
      const classTypeDistribution = Array.from(categoryMap.entries()).map(([name, value]) => ({
        name,
        value,
        color: getRandomColor(name)
      }));
      // Ordre chronologique croissant : le mois courant est le DERNIER élément.
      // L'ancien tri (index de mois puis `.reverse()`) produisait un ordre
      // décroissant, et le delta « vs le mois dernier » était donc inversé.
      const activityData = monthBuckets.map(({ key, label }) => ({
        month: label,
        classes: monthlyData.get(key) || 0,
      }));

        const lastMonth = activityData[activityData.length -1];
        const previousMonth = activityData[activityData.length -2];
        let totalClassesDelta = 0;
        if (lastMonth && previousMonth && previousMonth.classes > 0) {
          totalClassesDelta = ((lastMonth.classes - previousMonth.classes) / previousMonth.classes) * 100;
        } else if (lastMonth && previousMonth) {
          totalClassesDelta = lastMonth.classes === 0 ? 0 : 100;
        }

      set({
        isLoading: false,
        error: null,
        totalClasses: completedBookings.length,
        averageRating: avgRating,
        streak: currentStreak,
        longestStreak: maxStreak,
        totalHours: Math.round(totalMinutes / 60),
        bookingHistory,
        classTypeDistribution,
        activityData,
        totalClassesDelta,
        firstSessionDate,
        memberSince: memberSinceHuman,
        uniqueClassTypes,
        participationRate,
        cancellationRate,
        avgSessionsPerWeek,
        badges,
      });
    } catch (err) {
      set({ isLoading: false, error: "Failed to fetch dashboard" });
      console.error(err);
    }
  }
}));

// Helper couleur
function getRandomColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    '#D946EF', '#F97316', '#0EA5E9', '#33C3F0', '#9b87f5', '#10B981', '#F59E0B', '#6366F1'
  ];
  return colors[Math.abs(hash) % colors.length];
}
